/**
 * Split-screen auth layout.
 *
 * Left panel  — always-Forest dark surface.
 * Right panel — Cream page background.
 *
 * Colors use inline CSS-variable styles (hsl(var(--palette-*))) rather than
 * Tailwind utility classes. This bypasses Tailwind JIT entirely and guarantees
 * the correct colors appear immediately without requiring a dev-server restart
 * after palette tokens are added to preset.ts.
 *
 * Once the dev server has been restarted and bg-forest / text-amber etc. are
 * generated, these can be migrated to Tailwind classes.
 */
import * as React from 'react';
import { BrandLogo } from '@finmin/ui';

/* Fallback values ensure colors render even if CSS variables haven't loaded */
const FOREST  = 'hsl(var(--palette-forest, 176 72% 17%))';   /* #0D4A45 */
const AMBER   = 'hsl(var(--palette-amber,   33 100% 50%))';  /* #FF8C03 */
const CREAM   = 'hsl(var(--palette-cream,   40 100% 98%))';  /* #FFFBF4 */

const PILLARS = [
  { label: 'Taxes',       detail: 'Planned, not filed at the last minute.' },
  { label: 'Investments', detail: 'Tracked, rebalanced, and working for you.' },
  { label: 'Insurance',   detail: 'Coverage gaps found before they cost you.' },
  { label: 'Retirement',  detail: 'A number, a date, a path to get there.' },
] as const;

interface AuthLayoutProps { children: React.ReactNode }

export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex">
      <HeroPanel />

      {/* ── Right: cream form panel ─────────────────────────────────────────── */}
      <main
        className="flex-1 flex flex-col items-center justify-center px-6 py-12 sm:px-10"
        style={{ backgroundColor: CREAM }}
      >
        <MobileWordmark />
        <div className="w-full max-w-[420px] animate-slide-up-fade">
          {children}
        </div>
        <footer
          className="mt-12 text-xs text-center"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          © {new Date().getFullYear()} FinminOS Private Limited. All rights reserved.
        </footer>
      </main>
    </div>
  );
}

// ── HeroPanel ─────────────────────────────────────────────────────────────────

function HeroPanel() {
  return (
    <aside
      aria-hidden="true"
      className="hidden lg:flex lg:w-[52%] xl:w-[48%] flex-col justify-between relative overflow-hidden"
      style={{ backgroundColor: FOREST }}
    >
      {/* Thin amber accent rule along the right edge */}
      <div
        className="absolute right-0 top-0 bottom-0 w-px pointer-events-none"
        style={{ backgroundColor: AMBER, opacity: 0.3 }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-12 py-14 xl:px-16">

        {/* Wordmark */}
        <Wordmark />

        {/* Hero copy */}
        <div className="mt-auto mb-auto pt-16">
          {/* Main heading — Cream on Forest */}
          <h1
            className="font-display font-medium text-5xl xl:text-[3.5rem] leading-[1.05] tracking-tight"
            style={{ color: CREAM }}
          >
            One System.
          </h1>
          {/* Sub-heading — Amber italic brand signature */}
          <p
            className="font-display italic font-normal text-4xl xl:text-[2.75rem] leading-tight mt-2"
            style={{ color: AMBER }}
          >
            From Income to Wealth.
          </p>
          {/* Body copy — Cream at 70% opacity */}
          <p
            className="mt-7 text-[17px] xl:text-[18px] leading-relaxed max-w-[440px]"
            style={{ color: CREAM, opacity: 0.75 }}
          >
            We don't file your taxes. We bring your financial life — taxes,
            investments, insurance, ESOPs, and retirement in one place.
          </p>
        </div>

        {/* Pillars */}
        <ul className="mt-auto space-y-5 pb-2">
          {PILLARS.map(({ label, detail }) => (
            <li key={label} className="flex items-start gap-3.5">
              <span
                className="mt-[10px] h-[8px] w-[8px] shrink-0 rounded-full"
                style={{ backgroundColor: AMBER }}
              />
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                <span className="text-base font-semibold" style={{ color: CREAM }}>
                  {label}
                </span>
                <span className="text-sm" style={{ color: CREAM, opacity: 0.7 }}>
                  {detail}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

// ── Wordmark ──────────────────────────────────────────────────────────────────

function Wordmark() {
  return (
    <div className="flex items-center">
      <BrandLogo wordColor="cream" height={44} />
    </div>
  );
}

// ── MobileWordmark ────────────────────────────────────────────────────────────

function MobileWordmark() {
  return (
    <div className="flex lg:hidden items-center mb-8 self-start">
      <BrandLogo wordColor="forest" height={36} />
    </div>
  );
}
